import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { QrCode, User, Plus, Camera, LogOut, Check, RotateCcw } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/Navbar";
import { toast } from "sonner";

export default function EmployeeScanner() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [manual, setManual] = useState("");
  const [customer, setCustomer] = useState(null);
  const [recent, setRecent] = useState([]);
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [rewards, setRewards] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [lastTxn, setLastTxn] = useState(null);
  const scannerRef = useRef(null);

  useEffect(() => { api.get("/rewards").then((r) => setRewards(r.data)); return () => stopScan(); }, []);

  const lookup = async (token) => {
    try {
      const { data } = await api.post("/employee/lookup", { qr_token: token });
      setCustomer(data.customer); setRecent(data.recent); setLastTxn(null);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); setCustomer(null); }
  };

  const startScan = async () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const html5 = new Html5Qrcode("qr-reader");
        scannerRef.current = html5;
        await html5.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 },
          (decoded) => { stopScan(); setManual(decoded); lookup(decoded); },
          () => {});
      } catch (e) { toast.error("Kamera nicht verfügbar. Bitte Token manuell eingeben."); setScanning(false); }
    }, 100);
  };
  const stopScan = async () => {
    try { if (scannerRef.current) { await scannerRef.current.stop(); scannerRef.current.clear(); scannerRef.current = null; } } catch {}
    setScanning(false);
  };

  const addPoints = async () => {
    if (!customer || !amount) return;
    try {
      const { data } = await api.post("/employee/add-points", { customer_id: customer.id, amount: Number(amount), note });
      toast.success(`+${data.points_added} Punkte hinzugefügt`);
      setCustomer({ ...customer, points: data.new_balance }); setLastTxn(data.transaction_id); setAmount(""); setNote("");
      lookup(customer.qr_token);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const reverse = async () => {
    if (!lastTxn) return;
    try { await api.post("/employee/reverse", { transaction_id: lastTxn }); toast.success("Transaktion storniert"); setLastTxn(null); lookup(customer.qr_token); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const redeemReward = async (rid) => {
    try { const { data } = await api.post("/employee/redeem-reward", { customer_id: customer.id, reward_id: rid }); toast.success(`Prämie eingelöst! Code: ${data.code}`); lookup(customer.qr_token); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-[#0F1420] text-white">
      <header className="border-b border-white/10 px-4 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3"><div className="bg-white rounded-lg px-2 py-1"><Logo /></div><span className="text-sm text-gray-400">Mitarbeiter-Scanner</span></div>
        <button onClick={() => { logout(); navigate("/login"); }} className="flex items-center gap-2 text-sm text-gray-400 hover:text-white" data-testid="scanner-logout"><LogOut className="w-4 h-4" /> Abmelden</button>
      </header>

      <div className="max-w-lg mx-auto px-4 py-8 space-y-6">
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
          <h1 className="text-xl font-bold flex items-center gap-2"><QrCode className="w-5 h-5 text-[#E0243A]" /> QR-Code scannen</h1>
          {scanning ? (
            <div className="mt-4">
              <div id="qr-reader" className="rounded-xl overflow-hidden" />
              <button onClick={stopScan} className="mt-3 w-full py-2.5 rounded-xl bg-white/10 hover:bg-white/20 font-medium">Abbrechen</button>
            </div>
          ) : (
            <button onClick={startScan} data-testid="scanner-start" className="mt-4 w-full py-3 rounded-xl bg-[#E0243A] hover:bg-[#C81E33] font-semibold flex items-center justify-center gap-2"><Camera className="w-5 h-5" /> Kamera starten</button>
          )}
          <div className="mt-4 flex gap-2">
            <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="QR-Token manuell eingeben (z.B. HLM-...)" data-testid="scanner-manual-input" className="flex-1 px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder:text-gray-500" />
            <button onClick={() => lookup(manual)} data-testid="scanner-lookup" className="px-5 rounded-xl bg-white/10 hover:bg-white/20 font-medium">Suchen</button>
          </div>
        </div>

        {customer && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4" data-testid="scanner-customer">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#E0243A]/20 flex items-center justify-center"><User className="w-6 h-6 text-[#E0243A]" /></div>
              <div><div className="font-semibold text-lg">{customer.name}</div><div className="text-sm text-gray-400">{customer.segment}</div></div>
              <div className="ml-auto text-right"><div className="text-xs text-gray-400">Punkte</div><div className="text-2xl font-bold text-[#E0243A]" data-testid="scanner-points">{customer.points?.toLocaleString("de-DE")}</div></div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Einkauf €" data-testid="scanner-amount" className="px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder:text-gray-500" />
              <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Notiz (optional)" className="px-4 py-3 rounded-xl bg-white/10 border border-white/10 text-white placeholder:text-gray-500" />
            </div>
            {amount && <div className="text-sm text-gray-400">≈ {Math.round(Number(amount))} Punkte werden gutgeschrieben</div>}
            <button onClick={addPoints} data-testid="scanner-add-points" className="w-full py-3 rounded-xl bg-[#E0243A] hover:bg-[#C81E33] font-semibold flex items-center justify-center gap-2"><Plus className="w-5 h-5" /> Punkte hinzufügen</button>
            {lastTxn && <button onClick={reverse} data-testid="scanner-reverse" className="w-full py-2.5 rounded-xl border border-white/20 hover:bg-white/10 text-sm flex items-center justify-center gap-2"><RotateCcw className="w-4 h-4" /> Letzte Transaktion stornieren</button>}

            <div className="pt-3 border-t border-white/10">
              <div className="text-sm font-semibold mb-2">Prämie einlösen</div>
              <div className="grid grid-cols-1 gap-2">
                {rewards.map((r) => (
                  <button key={r.id} disabled={customer.points < r.points_required} onClick={() => redeemReward(r.id)} data-testid={`scanner-redeem-${r.id}`}
                    className="flex items-center justify-between px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 disabled:opacity-40 hover:bg-white/10 text-sm">
                    <span>{r.name}</span><span className="text-[#E0243A] font-semibold">{r.points_required} Pkt.</span>
                  </button>
                ))}
              </div>
            </div>

            {recent.length > 0 && (
              <div className="pt-3 border-t border-white/10 text-sm">
                <div className="text-gray-400 mb-2">Letzte Aktivität</div>
                {recent.slice(0, 4).map((t) => (
                  <div key={t.id} className="flex justify-between py-1"><span className="text-gray-300">{t.note}</span><span className={t.points >= 0 ? "text-emerald-400" : "text-[#E0243A]"}>{t.points >= 0 ? "+" : ""}{t.points}</span></div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
